import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Envelope, PaperPlaneTilt, PencilSimple, Eye, ClockCounterClockwise,
  ArrowClockwise, CheckCircle, CursorClick, EnvelopeOpen, Lightning, Copy,
} from "@phosphor-icons/react";

const CAT_COLOR = {
  lifecycle: "bg-blue-100 text-blue-800",
  security: "bg-red-100 text-red-800",
  marketing: "bg-purple-100 text-purple-800",
  internal: "bg-amber-100 text-amber-800",
  engagement: "bg-emerald-100 text-emerald-800",
  transactional: "bg-indigo-100 text-indigo-800",
};

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="flex items-center gap-2 min-w-[70px]">
    <Icon size={16} weight="bold" className="text-[#0B3D91] shrink-0" />
    <div className="leading-tight">
      <div className="text-sm font-bold text-foreground">{value}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}{sub ? ` · ${sub}` : ""}</div>
    </div>
  </div>
);

function TemplateCard({ t, onToggle, onEdit, onPreview }) {
  const m = t.metrics || {};
  return (
    <div className="border border-border bg-card rounded-sm p-4 flex flex-col gap-3 hover:border-[#0B3D91]/40 transition-colors" data-testid={`tpl-card-${t.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-foreground truncate">{t.name}</span>
            <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-sm capitalize ${CAT_COLOR[t.category] || "bg-secondary text-muted-foreground"}`}>{t.category}</span>
            {t.custom ? <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-teal-100 text-teal-800">Custom</span> : null}
            {!t.editable ? <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-secondary text-muted-foreground inline-flex items-center gap-0.5"><Lightning size={10} weight="fill" />System</span> : null}
          </div>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Switch checked={t.enabled} onCheckedChange={(v) => onToggle(t, v)} data-testid={`tpl-toggle-${t.key}`} />
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap border-t border-border pt-3">
        <Stat icon={PaperPlaneTilt} label="Sent" value={m.sent ?? 0} />
        <Stat icon={EnvelopeOpen} label="Opens" value={`${m.open_rate ?? 0}%`} sub={m.opened ?? 0} />
        <Stat icon={CursorClick} label="Clicks" value={`${m.click_rate ?? 0}%`} sub={m.clicked ?? 0} />
        <Stat icon={CheckCircle} label="Conv." value={`${m.conversion_rate ?? 0}%`} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="rounded-sm gap-1 h-8 text-xs" onClick={() => onPreview(t)} data-testid={`tpl-preview-${t.key}`}>
          <Eye size={13} weight="bold" /> Preview
        </Button>
        {t.editable ? (
          <Button size="sm" variant="outline" className="rounded-sm gap-1 h-8 text-xs" onClick={() => onEdit(t)} data-testid={`tpl-edit-${t.key}`}>
            <PencilSimple size={13} weight="bold" /> Edit
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function EmailAutomation() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [versions, setVersions] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/email-templates")
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => toast.error("Could not load email templates"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onToggle = async (t, enabled) => {
    setTemplates((prev) => prev.map((x) => (x.key === t.key ? { ...x, enabled } : x)));
    try {
      await api.patch(`/admin/email-templates/${t.key}/toggle`, { enabled });
      toast.success(`${t.name} ${enabled ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Could not update");
      setTemplates((prev) => prev.map((x) => (x.key === t.key ? { ...x, enabled: !enabled } : x)));
    }
  };

  const openEditor = async (t) => {
    setEditing(t);
    setDraft({ subject: t.subject || "", heading: t.heading || "", body_html: t.body_html || "", cta_text: t.cta_text || "", cta_url: t.cta_url || "" });
    setPreviewHtml("");
    try {
      const r = await api.get(`/admin/email-templates/${t.key}`);
      setVersions(r.data.versions || []);
    } catch { setVersions([]); }
  };

  const refreshDraftPreview = async () => {
    if (!editing) return;
    try {
      const r = await api.post(`/admin/email-templates/${editing.key}/preview`, draft);
      setPreviewHtml(r.data.html || "");
    } catch { toast.error("Preview failed"); }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${editing.key}`, draft);
      toast.success("Template saved — new version created");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const revert = async (v) => {
    if (!editing) return;
    try {
      const r = await api.post(`/admin/email-templates/${editing.key}/revert/${v.version}`);
      setDraft({ subject: r.data.subject || "", heading: r.data.heading || "", body_html: r.data.body_html || "", cta_text: r.data.cta_text || "", cta_url: r.data.cta_url || "" });
      toast.success(`Reverted to v${v.version}`);
      const d = await api.get(`/admin/email-templates/${editing.key}`);
      setVersions(d.data.versions || []);
      load();
    } catch { toast.error("Could not revert"); }
  };

  const openPreview = async (t) => {
    setPreview(t);
    setPreviewHtml("");
    try {
      const r = await api.post(`/admin/email-templates/${t.key}/preview`, {});
      setPreviewHtml(r.data.html || "");
      if (!r.data.html && r.data.note) toast.info(r.data.note);
    } catch { toast.error("Preview failed"); }
  };

  const sendTest = async (key) => {
    if (!testTo.includes("@")) { toast.error("Enter a valid email address"); return; }
    setTesting(true);
    try {
      const r = await api.post(`/admin/email-templates/${key}/test`, { to: testTo });
      if (r.data.sent) toast.success(`Test email sent to ${r.data.to}`);
      else toast.error(r.data.reason || "Send failed — check provider config");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test");
    } finally { setTesting(false); }
  };

  return (
    <div data-testid="email-automation-page">
      <PageHeader title="Email Automation" subtitle="Manage every RecoverOS email — edit content, toggle on/off, preview, test and track performance">
        <Button variant="ghost" size="sm" onClick={load} className="gap-1 h-9" data-testid="tpl-refresh"><ArrowClockwise size={14} /> Refresh</Button>
      </PageHeader>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground">Loading templates…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((t) => (
            <TemplateCard key={t.key} t={t} onToggle={onToggle} onEdit={openEditor} onPreview={openPreview} />
          ))}
        </div>
      )}

      {/* Editor drawer */}
      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="tpl-editor">
          <SheetHeader><SheetTitle>Edit — {editing?.name}</SheetTitle></SheetHeader>
          {editing && (
            <div className="mt-4 space-y-4">
              <p className="text-xs text-muted-foreground">Use <code className="px-1 bg-secondary rounded">{"{{variable}}"}</code> placeholders. Available: {(editing.variables || []).map((v) => <code key={v} className="px-1 mx-0.5 bg-secondary rounded text-[11px]">{`{{${v}}}`}</code>)}</p>
              <div>
                <Label>Subject line</Label>
                <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="rounded-sm mt-1" data-testid="tpl-field-subject" />
              </div>
              <div>
                <Label>In-email heading</Label>
                <Input value={draft.heading} onChange={(e) => setDraft({ ...draft, heading: e.target.value })} className="rounded-sm mt-1" data-testid="tpl-field-heading" />
              </div>
              <div>
                <Label>Body (HTML allowed)</Label>
                <Textarea value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} rows={8} className="rounded-sm mt-1 font-mono text-xs" data-testid="tpl-field-body" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Button text</Label>
                  <Input value={draft.cta_text} onChange={(e) => setDraft({ ...draft, cta_text: e.target.value })} className="rounded-sm mt-1" data-testid="tpl-field-cta-text" />
                </div>
                <div>
                  <Label>Button URL</Label>
                  <Input value={draft.cta_url} onChange={(e) => setDraft({ ...draft, cta_url: e.target.value })} className="rounded-sm mt-1" data-testid="tpl-field-cta-url" />
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={save} disabled={saving} className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" data-testid="tpl-save"><CheckCircle size={15} weight="bold" /> {saving ? "Saving…" : "Save changes"}</Button>
                <Button onClick={refreshDraftPreview} variant="outline" className="rounded-sm gap-1.5" data-testid="tpl-refresh-preview"><Eye size={15} weight="bold" /> Update preview</Button>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="rounded-sm w-52 h-9" data-testid="tpl-test-to" />
                <Button onClick={() => sendTest(editing.key)} disabled={testing} variant="outline" className="rounded-sm gap-1.5" data-testid="tpl-test-send"><PaperPlaneTilt size={15} weight="bold" /> {testing ? "Sending…" : "Send test"}</Button>
              </div>

              {previewHtml ? (
                <div>
                  <Label className="flex items-center gap-1"><Eye size={14} /> Live preview</Label>
                  <iframe title="preview" srcDoc={previewHtml} className="w-full h-[520px] border border-border rounded-sm mt-1 bg-white" data-testid="tpl-editor-preview-frame" />
                </div>
              ) : null}

              {versions.length > 0 && (
                <div className="pt-2 border-t border-border">
                  <Label className="flex items-center gap-1"><ClockCounterClockwise size={14} /> Version history</Label>
                  <div className="mt-2 space-y-1">
                    {versions.map((v) => (
                      <div key={v.version_id} className="flex items-center justify-between text-xs border border-border rounded-sm px-3 py-2" data-testid={`tpl-version-${v.version}`}>
                        <span>v{v.version} · <span className="text-muted-foreground">{fmtDateTime(v.snapshot_at)} · {v.updated_by}</span></span>
                        <Button size="sm" variant="ghost" className="h-6 gap-1 text-xs" onClick={() => revert(v)} data-testid={`tpl-revert-${v.version}`}><ArrowClockwise size={12} /> Restore</Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Read-only preview drawer */}
      <Sheet open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="tpl-preview-drawer">
          <SheetHeader><SheetTitle>Preview — {preview?.name}</SheetTitle></SheetHeader>
          <div className="mt-4 flex items-center gap-2">
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="rounded-sm w-52 h-9" data-testid="tpl-preview-test-to" />
            <Button onClick={() => preview && sendTest(preview.key)} disabled={testing} variant="outline" className="rounded-sm gap-1.5" data-testid="tpl-preview-test-send"><PaperPlaneTilt size={15} weight="bold" /> {testing ? "Sending…" : "Send test"}</Button>
          </div>
          {previewHtml ? (
            <iframe title="preview" srcDoc={previewHtml} className="w-full h-[600px] border border-border rounded-sm mt-4 bg-white" data-testid="tpl-preview-frame" />
          ) : (
            <div className="p-10 text-center text-muted-foreground mt-4"><Envelope size={28} className="mx-auto mb-2 opacity-40" />No sample available yet — this email is composed in code and will preview once one has been sent.</div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
